import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  CampaignJoinInfo,
  CampaignMemberRole,
  CreateCampaignInput,
  PublicCampaignJoinInfo,
  UpdateCampaignInput,
  UserCampaignRecord,
  UserCampaignSummary,
} from "@west-marches/shared";
import {
  createUserCampaign,
  deleteUserCampaign,
  getCampaignJoinInfo,
  getPublicCampaignJoinInfo,
  getUserCampaignByShortId,
  joinCampaignByCode,
  listUserCampaigns,
  removeCampaignMember,
  updateCampaignJoinCode,
  updateMemberRole,
  updateUserCampaign,
} from "@/lib/campaignsClient";
import { queryKeys } from "@/lib/queryKeys";

function updateCampaignList(
  current: UserCampaignSummary[] | undefined,
  targetId: string,
  updater: (campaign: UserCampaignSummary) => UserCampaignSummary,
): UserCampaignSummary[] {
  return (current ?? []).map((campaign) => (campaign.id === targetId ? updater(campaign) : campaign));
}

export function useUserCampaignsQuery(
  initialCampaigns?: UserCampaignSummary[],
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.campaigns.list(),
    queryFn: listUserCampaigns,
    initialData: initialCampaigns,
    enabled,
  });
}

export function useCampaignByShortIdQuery(
  shortId: string,
  initialCampaign?: UserCampaignRecord,
  enabled = true,
  options: { asPlayer?: boolean } = {},
) {
  const asPlayer = Boolean(options.asPlayer);
  return useQuery({
    queryKey: queryKeys.campaigns.byShortId(shortId, asPlayer),
    queryFn: () => getUserCampaignByShortId(shortId, { asPlayer }),
    initialData: asPlayer ? undefined : initialCampaign,
    enabled,
  });
}

export function usePublicCampaignJoinInfoQuery(shortId: string, enabled = true) {
  return useQuery<PublicCampaignJoinInfo>({
    queryKey: queryKeys.campaigns.publicJoinInfo(shortId),
    queryFn: () => getPublicCampaignJoinInfo(shortId),
    enabled: enabled && shortId.length > 0,
    retry: false,
  });
}

export function useCampaignJoinInfoQuery(shortId: string, enabled = true) {
  return useQuery<CampaignJoinInfo>({
    queryKey: queryKeys.campaigns.joinInfo(shortId),
    queryFn: () => getCampaignJoinInfo(shortId),
    enabled: enabled && shortId.length > 0,
    retry: false,
  });
}

export function useCreateCampaignMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateCampaignInput) => createUserCampaign(input),
    onSuccess(createdCampaign) {
      queryClient.setQueryData<UserCampaignSummary[]>(queryKeys.campaigns.list(), (current) => [
        createdCampaign,
        ...(current ?? []),
      ]);
      queryClient.setQueryData(queryKeys.campaigns.byShortId(createdCampaign.shortId), createdCampaign);
    },
  });
}

export function useUpdateCampaignMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ campaignId, input }: { campaignId: string; input: UpdateCampaignInput }) =>
      updateUserCampaign(campaignId, input),
    onSuccess(updatedCampaign) {
      queryClient.setQueryData<UserCampaignSummary[]>(queryKeys.campaigns.list(), (current) =>
        updateCampaignList(current, updatedCampaign.id, () => updatedCampaign),
      );
      queryClient.setQueryData<UserCampaignRecord | undefined>(
        queryKeys.campaigns.byShortId(updatedCampaign.shortId),
        (current) => current ? { ...current, ...updatedCampaign } : current,
      );
    },
  });
}

export function useDeleteCampaignMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (campaignId: string) => deleteUserCampaign(campaignId),
    onSuccess(_, deletedCampaignId) {
      queryClient.setQueryData<UserCampaignSummary[]>(queryKeys.campaigns.list(), (current) =>
        (current ?? []).filter((campaign) => campaign.id !== deletedCampaignId),
      );
    },
  });
}

export function useJoinCampaignMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ shortId, joinCode }: { shortId: string; joinCode: string }) =>
      joinCampaignByCode(shortId, joinCode),
    onSuccess(joinedCampaign) {
      queryClient.setQueryData(queryKeys.campaigns.byShortId(joinedCampaign.shortId), joinedCampaign);
      queryClient.setQueryData<UserCampaignSummary[]>(queryKeys.campaigns.list(), (current) => [
        joinedCampaign,
        ...(current ?? []),
      ]);
    },
  });
}

export function useUpdateJoinCodeMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      campaignId,
      input,
    }: {
      campaignId: string;
      input: { regenerate?: boolean; selfJoinEnabled?: boolean };
    }) => updateCampaignJoinCode(campaignId, input),
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.list() });
    },
  });
}

export function useUpdateMemberRoleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      campaignId,
      userId,
      role,
    }: {
      campaignId: string;
      userId: string;
      role: CampaignMemberRole;
    }) => updateMemberRole(campaignId, userId, role),
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.list() });
    },
  });
}

export function useRemoveMemberMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ campaignId, userId }: { campaignId: string; userId: string }) =>
      removeCampaignMember(campaignId, userId),
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.list() });
    },
  });
}
